import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Users, Calendar, Workflow, ClipboardList, Banknote,
  CreditCard, Settings, Search, Inbox as InboxIcon,
  CheckSquare, ListFilter, Link as LinkIcon,
  Mail as MailIcon, Megaphone, BarChart3,
  CalendarDays, Sparkles, FileText, ShoppingCart, TrendingUp, Video,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import type { Contact } from "@/lib/types";
import { Avatar } from "@/components/ui/Avatar";
import { fullName } from "@/lib/format";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { OnlineBookingBell } from "@/features/notifications/OnlineBookingBell";
import { canAny, isPracticeStaff, useMe, type Capability } from "@/lib/me";
import { BookButton } from "@/features/scheduling/BookButton";
import { AccountMenu } from "@/features/account/AccountMenu";
import { OnShift } from "@/features/timeclock/OnShift";
import { FrontDeskChatButton } from "@/features/teamchat/FrontDeskChat";
import { LG, useMediaQuery } from "@/lib/media";

export interface SubItem {
  to: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Section(s) this page needs; any one is enough. */
  needs?: Capability | Capability[];
}
export interface NavSection {
  /** Section(s) required to see this nav entry at all; any one is enough. */
  needs?: Capability | Capability[];
  /** Path used as the section's "default" page (clicking the top-level
   *  label navigates here). For single-page sections, this is the page. */
  defaultTo: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** All paths that "belong" to this section. Used for active-tab matching. */
  paths: string[];
  /** Sub-pages — when present, a sub-nav row renders below the top nav. */
  subnav?: SubItem[];
}

/**
 * The full app IA. Order matters — sections render left-to-right.
 *
 * Sub-pages are listed in the order they appear in the secondary nav. The
 * first sub-page is the section's default landing page when the user
 * clicks the top-level tab.
 */
export const SECTIONS: NavSection[] = [
  // Ordered to match how she moves through Boulevard, because that is the
  // muscle memory she already has: Front Desk · Calendar · Messages · Sales ·
  // Clients · Reports · Marketing · Manage.
  {
    defaultTo: "/frontdesk",
    label: "Front desk",
    needs: "schedule",
    icon: ClipboardList,
    paths: ["/frontdesk"],
  },
  {
    defaultTo: "/schedule",
    label: "Calendar",
    needs: "schedule",
    icon: CalendarDays,
    paths: ["/schedule", "/calendar", "/scheduling-links"],
    subnav: [
      { to: "/schedule",          label: "Day",           icon: CalendarDays },
      { to: "/calendar",          label: "Month",         icon: Calendar },
      { to: "/scheduling-links",  label: "Booking links", icon: LinkIcon },
    ],
  },
  {
    defaultTo: "/inbox",
    label: "Messages",
    needs: "messages",
    icon: InboxIcon,
    paths: ["/inbox"],
  },
  {
    defaultTo: "/checkout",
    label: "Sales",
    needs: ["checkout", "reports"],
    icon: ShoppingCart,
    paths: ["/checkout", "/register", "/payments"],
    subnav: [
      { to: "/checkout", label: "Check out",   icon: ShoppingCart, needs: "checkout" },
      { to: "/register", label: "Cash drawer", icon: Banknote,     needs: ["checkout", "reports"] },
      { to: "/payments", label: "Payments",    icon: CreditCard,   needs: ["reports", "checkout"] },
    ],
  },
  {
    defaultTo: "/contacts",
    label: "Clients",
    needs: "clients",
    icon: Users,
    paths: ["/contacts", "/lists", "/tasks"],
    subnav: [
      { to: "/contacts", label: "Clients",   icon: Users },
      { to: "/lists",    label: "Audiences", icon: ListFilter },
      { to: "/tasks",    label: "Tasks",     icon: CheckSquare },
    ],
  },
  {
    defaultTo: "/reports",
    label: "Reports",
    needs: "reports",
    icon: BarChart3,
    paths: ["/reports"],
  },
  {
    // "My marketing tab shows emails? Not the this?" — it opened on the
    // mailbox, so the campaigns she was looking for were one more click away
    // and the section looked like it had nothing in it. Marketing means
    // campaigns; the mailbox is still right there underneath.
    defaultTo: "/campaigns",
    label: "Marketing",
    needs: "marketing",
    icon: Megaphone,
    paths: ["/mail", "/campaigns"],
    subnav: [
      { to: "/campaigns", label: "Campaigns", icon: Megaphone },
      { to: "/mail",      label: "Email",     icon: MailIcon },
    ],
  },
  // Its own section rather than a line under Marketing: for the person who runs
  // her ads this is the only thing in the software, and Marketing can send texts
  // to her clients.
  {
    defaultTo: "/ads",
    label: "Advertising",
    needs: "ads",
    icon: TrendingUp,
    paths: ["/ads"],
  },
  // "can we have a tab to the left that has the stationed link for each
  // provider to go into when needed … If it's on the left I also can have
  // access?" — the sidebar only shows top-level sections, so a tab on the left
  // is one of these, not a line under Calendar.
  {
    defaultTo: "/video-rooms",
    label: "Video rooms",
    needs: "schedule",
    icon: Video,
    paths: ["/video-rooms"],
  },
  {
    defaultTo: "/services",
    label: "Manage",
    // Somebody given only the forms still needs the door they are behind.
    needs: ["settings", "forms"],
    icon: Sparkles,
    paths: ["/services", "/forms", "/automations", "/team"],
    subnav: [
      { to: "/services",    label: "Menu & seats", icon: Sparkles, needs: "settings" },
      { to: "/team",        label: "Team",         icon: Users,    needs: "settings" },
      { to: "/forms",       label: "Forms",        icon: FileText, needs: ["settings", "forms"] },
      { to: "/automations", label: "Automations",  icon: Workflow, needs: "settings" },
    ],
  },
  // Settings intentionally has no section entry — it renders in the SideNav
  // footer on desktop and as the last chip of the mobile bar.
];

/**
 * The sections the nav should actually SHOW. The store module is dormant on
 * most accounts (they don't sell anything), so its section stays out of the
 * nav until the backend says it's in use — enabled in Store settings, or
 * products/orders exist. The /store routes stay mounted regardless, so a
 * direct link (or the Settings toggle) always works.
 */
export function useNavSections(): NavSection[] {
  const me = useMe();
  // Until we know who this is, show everything — the owner is most of the
  // traffic and a flickering nav is worse than a moment of over-showing.
  if (!me) return SECTIONS;
  return SECTIONS
    .filter((sec) => canAny(me, sec.needs))
    .map((sec) => {
      if (!sec.subnav) return sec;
      const subnav = sec.subnav.filter((sub) => canAny(me, sub.needs));
      // A section whose default page is hidden lands on its first open one.
      const defaultTo = subnav.some((sub) => sub.to === sec.defaultTo) ? sec.defaultTo : (subnav[0]?.to ?? sec.defaultTo);
      return { ...sec, subnav, defaultTo };
    });
}

/** Where to land after sign-in: the first section this person can open. */
export function useHomePath(): string | null {
  const me = useMe();
  const sections = useNavSections();
  if (!me) return null;
  return sections[0]?.defaultTo ?? "/settings";
}

/** Match the section a path belongs to. Returns null for paths that live
 *  outside the section list (e.g. /settings, which has its own links). */
export function matchSection(pathname: string): NavSection | null {
  // Most specific match wins — sort sections by longest matching prefix.
  let best: NavSection | null = null;
  let bestLen = -1;
  for (const s of SECTIONS) {
    for (const p of s.paths) {
      if (p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(p + "/")) {
        if (p.length > bestLen) { best = s; bestLen = p.length; }
      }
    }
  }
  return best;
}

export function TopNav({ workspaceName }: { workspaceName?: string }) {
  const { pathname } = useLocation();
  // Where the chat button belongs. Below lg it is a chip in this bar; at lg
  // and up CrmLayout floats it instead.
  const desktop = useMediaQuery(LG);
  const sections = useNavSections();
  const me = useMe();
  const staff = isPracticeStaff(me);
  // The per-person copy of the section (its sub-pages filtered to what they
  // can open), matched by label.
  const matched = matchSection(pathname);
  const activeSection = matched ? (sections.find((s) => s.label === matched.label) ?? null) : null;

  return (
    <header className="sticky top-0 z-30 border-b border-zinc-800/70 bg-zinc-950/95 backdrop-blur supports-backdrop-filter:bg-zinc-950/80">
      <div className="mx-auto flex h-12 max-w-screen-2xl items-center gap-1 px-2 sm:px-4 lg:px-6">
        {/* Primary tabs — below sm only, as icon chips (evenly distributed
            via flex-1 — no horizontal scroll). At sm+ the SideNav
            carries the primary navigation and this row becomes a toolbar:
            sub-nav pills for the active section + search + notifications. */}
        <nav className="flex flex-1 items-center gap-0.5 sm:hidden">
          {sections.map((s) => {
            const isActive = s === activeSection;
            const Icon = s.icon;
            return (
              <NavLink
                key={s.defaultTo}
                to={s.defaultTo}
                end={s.defaultTo === "/"}
                aria-label={s.label}
                className={cn(
                  "group relative inline-flex flex-1 items-center justify-center rounded-lg px-0.5 py-1.5 transition-colors",
                  isActive
                    ? "bg-zinc-800/70 text-zinc-100"
                    : "text-zinc-400 hover:bg-zinc-800/40 hover:text-zinc-100"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <Flyout label={s.label} side="bottom" />
              </NavLink>
            );
          })}
          {/* Settings rides in the chip row below sm; at sm+ it lives in
              the SideNav footer. */}
          {canAny(me, ["settings", "staff"]) && (
          <NavLink
            to="/settings"
            aria-label="Settings"
            className={({ isActive }) =>
              cn(
                "group relative inline-flex flex-1 items-center justify-center rounded-lg px-0.5 py-1.5 transition-colors",
                isActive
                  ? "bg-zinc-800/70 text-zinc-100"
                  : "text-zinc-400 hover:bg-zinc-800/40 hover:text-zinc-100"
              )
            }
          >
            <Settings className="h-4 w-4 shrink-0" />
            <Flyout label="Settings" side="bottom" />
          </NavLink>
          )}
        </nav>

        {/* Desktop toolbar left half: the active section's sub-pages (the
            sub-row below carries these under sm), or its name when it has
            no sub-pages — the bar always says where you are. */}
        <div className="hidden sm:flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
          {activeSection?.subnav && activeSection.subnav.length > 1 ? (
            activeSection.subnav.map((sub) => <SubPill key={sub.to} sub={sub} />)
          ) : (
            <span className="truncate px-1 text-sm font-semibold text-zinc-100">
              {activeSection ? activeSection.label : pathname.startsWith("/settings") ? "Settings" : ""}
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {/* Booking is the single most frequent action in the building, so it
              sits in the chrome on every screen rather than only on the
              calendar. Icon-only under sm to keep the phone bar uncluttered. */}
          {staff && <BookButton className="hidden sm:inline-flex" />}
          {staff && <BookButton className="sm:hidden" label="" />}
          {staff && <GlobalSearch />}
          {/* "see at the top right all who are blocked in … so we know who
              is in" — hidden entirely when nobody is on the clock. */}
          <OnShift />
          {/* On a phone the chat lives here, out of the way of Send. From lg
              up CrmLayout floats it instead — it cannot float from inside this
              header, whose backdrop-blur makes it the containing block for
              anything fixed. */}
          {staff && !desktop && <FrontDeskChatButton variant="toolbar" />}
          <OnlineBookingBell />
          <NotificationBell />
          {/* "how do we log out" — the way out, in the corner everybody
              already looks, on every screen and every width. */}
          <AccountMenu />
        </div>
      </div>

      {/* Secondary nav row — below sm only (at sm+ the pills live in the
          toolbar row above). Icon-only chips, evenly distributed (no
          horizontal scroll), hover flyouts carry the labels — even
          Store's eight fit on a 360px phone. */}
      {activeSection?.subnav && activeSection.subnav.length > 1 && (
        <div className="border-t border-zinc-900/60 bg-zinc-950/95 sm:hidden">
          <div className="mx-auto flex h-9 max-w-screen-2xl items-center gap-0.5 px-2">
            {activeSection.subnav.map((sub) => (
              <SubPill key={sub.to} sub={sub} mobile />
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

/** One sub-page pill. Toolbar variant (sm+) shows the label (icon joins at
 *  lg); the mobile sub-row variant is an evenly-stretched icon chip whose
 *  label rides in a hover flyout. */
function SubPill({ sub, mobile }: { sub: SubItem; mobile?: boolean }) {
  const Icon = sub.icon;
  return (
    <NavLink
      to={sub.to}
      end
      aria-label={sub.label}
      className={({ isActive }) =>
        cn(
          "group relative inline-flex items-center justify-center gap-1.5 rounded-md py-1 text-xs font-medium whitespace-nowrap transition-colors",
          mobile ? "flex-1 px-2" : "px-2.5",
          isActive
            ? "bg-indigo-500/15 text-indigo-200"
            : "text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"
        )
      }
    >
      {/* Toolbar pills drop their icons until lg — Store's eight pills
          must fit beside the sidebar on an 830px window. */}
      {Icon && <Icon className={mobile ? "h-3.5 w-3.5" : "hidden lg:block h-3 w-3"} />}
      {mobile ? <Flyout label={sub.label} side="bottom" /> : <span>{sub.label}</span>}
    </NavLink>
  );
}

/** Hover label for icon-only nav controls. Parent needs `group relative`.
 *  side="right" flies out beside the collapsed SideNav rail; side="bottom"
 *  drops below the mobile chip bars. Pointer-events-none so it never
 *  swallows the click it's describing. */
export function Flyout({ label, side = "right" }: { label: string; side?: "right" | "bottom" }) {
  return (
    <span
      className={cn(
        "pointer-events-none absolute z-50 whitespace-nowrap rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs font-medium text-zinc-100 opacity-0 shadow-lg shadow-black/40 transition-opacity duration-100 group-hover:opacity-100",
        side === "right"
          ? "left-full top-1/2 ml-3 -translate-y-1/2"
          : "top-full left-1/2 mt-1.5 -translate-x-1/2"
      )}
    >
      {label}
    </span>
  );
}

function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  // Esc closes the popover. (No ⌘K shortcut — visible button is enough.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const h = setTimeout(() => {
      api.get<{ contacts: Contact[] }>("/contacts", { q: q.trim(), limit: 8 })
        .then((d) => setResults(d.contacts))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 150);
    return () => clearTimeout(h);
  }, [q]);

  return (
    <div ref={dropRef} className="relative shrink-0">
      {/*
        "Also can we search clients from front desk and calendar screen"

        It was on every screen already - as an unlabelled magnifying glass
        between Book and the bell, which she looked straight past. It says
        what it is now. Still an icon on a phone, where there is no room for
        the word and the toolbar is already understood.
      */}
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="group relative inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/50 px-2 text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-zinc-100 sm:px-2.5"
        aria-label="Search clients"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="hidden text-xs font-medium sm:inline">Search clients</span>
        {/* The word is on the button from sm up, so the hover label is only
            worth having on a phone, where it is an icon again. */}
        {!open && <span className="contents sm:hidden"><Flyout label="Search clients" side="bottom" /></span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 max-w-[92vw] rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40 animate-fade-in z-40">
          <div className="border-b border-zinc-800 p-2">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients by name, email or phone…"
              className="w-full bg-transparent px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {loading && <p className="p-3 text-xs text-zinc-500">Searching…</p>}
            {!loading && q && results.length === 0 && <p className="p-3 text-xs text-zinc-500">No matches.</p>}
            {!loading && !q && <p className="p-3 text-xs text-zinc-500">Start typing a client\u2019s name</p>}
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { navigate(`/contacts/${c.id}`, { state: { from: pathname } }); setOpen(false); setQ(""); }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-zinc-900"
              >
                <Avatar name={fullName(c)} email={c.email} src={c.avatar_url} size="xs" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-zinc-100">{fullName(c)}</p>
                  <p className="truncate text-xs text-zinc-500">{c.email || c.phone}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
